import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Checkbox,
  TableContainer,
  useColorModeValue,
} from '@chakra-ui/react';
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import { MerchantGroupedRow } from '../../hooks/useMerchantGrouping';
import { PlaidTransaction } from '../../types/transactions';

// ─── Types ─────────────────────────────────────────────────────────────────────

type SortColumn = 'month' | 'normalizedMerchant' | 'count' | 'totalAmount' | 'firstDate' | 'lastDate';
type GroupedMode = 'consolidated' | 'monthly';

interface MerchantGroupedViewProps {
  rows: MerchantGroupedRow[];
  mode?: GroupedMode;
  isEditable?: boolean;
  /** Called when a row is clicked — open the drill-down drawer with these transactions */
  onRowClick: (transactions: PlaidTransaction[], title: string) => void;
  /** Called when a group is selected for bulk edit (EDITABLE mode only) */
  onGroupSelect?: (ids: string[]) => void;
  /** Currently selected IDs — used to show checkboxes as checked */
  selectedIds?: Set<string>;
}

interface ConsolidatedMerchantRow {
  rowKey: string;
  month?: string;
  normalizedMerchant: string;
  count: number;
  totalAmount: number;
  firstDate: string;
  lastDate: string;
  sourceTransactions: PlaidTransaction[];
}

function formatSigned(val: number): string {
  if (val === 0) return '-';
  const abs = Math.abs(val);
  const fmt = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(abs);
  return val < 0 ? `-${fmt}` : fmt;
}

function formatDateUs(rawDate?: string | null): string {
  if (!rawDate) return '';
  const baseDate = rawDate.includes('T') ? rawDate.slice(0, 10) : rawDate;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(baseDate);
  if (!match) return baseDate;
  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
}

// ─── Component ─────────────────────────────────────────────────────────────────

export const MerchantGroupedView: React.FC<MerchantGroupedViewProps> = ({
  rows,
  mode = 'consolidated',
  isEditable = false,
  onRowClick,
  onGroupSelect,
  selectedIds = new Set(),
}) => {
  const [sortCol, setSortCol] = useState<SortColumn>('normalizedMerchant');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const headerBg = useColorModeValue('gray.50', 'gray.700');
  const headerColor = useColorModeValue('gray.600', 'gray.300');
  const rowHoverBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleSort = (col: SortColumn) => {
    if (sortCol === col) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir(col === 'count' || col === 'totalAmount' ? 'desc' : 'asc');
    }
  };

  const displayRows = useMemo<ConsolidatedMerchantRow[]>(() => {
    if (mode === 'monthly') {
      return rows.map((r) => ({
        rowKey: r.groupKey,
        month: r.month,
        normalizedMerchant: r.normalizedMerchant,
        count: r.count,
        totalAmount: r.totalAmount,
        firstDate: r.firstDate,
        lastDate: r.lastDate,
        sourceTransactions: r.sourceTransactions,
      }));
    }

    const map = new Map<string, ConsolidatedMerchantRow>();

    for (const row of rows) {
      const merchantDisplay = row.normalizedMerchant.trim();
      const merchantKey = merchantDisplay.toLowerCase();
      const existing = map.get(merchantKey);
      if (existing) {
        existing.count += row.count;
        existing.totalAmount += row.totalAmount;
        if (row.firstDate < existing.firstDate) existing.firstDate = row.firstDate;
        if (row.lastDate > existing.lastDate) existing.lastDate = row.lastDate;
        existing.sourceTransactions.push(...row.sourceTransactions);
      } else {
        map.set(merchantKey, {
          rowKey: merchantKey,
          normalizedMerchant: merchantDisplay,
          count: row.count,
          totalAmount: row.totalAmount,
          firstDate: row.firstDate,
          lastDate: row.lastDate,
          sourceTransactions: [...row.sourceTransactions],
        });
      }
    }

    return [...map.values()];
  }, [rows, mode]);

  const sorted = useMemo(() => {
    return [...displayRows].sort((a, b) => {
      let cmp = 0;
      switch (sortCol) {
        case 'month':
          cmp = (a.month || '').localeCompare(b.month || '');
          break;
        case 'normalizedMerchant':
          cmp = a.normalizedMerchant.localeCompare(b.normalizedMerchant);
          break;
        case 'count':
          cmp = a.count - b.count;
          break;
        case 'totalAmount':
          cmp = a.totalAmount - b.totalAmount;
          break;
        case 'firstDate':
          cmp = a.firstDate.localeCompare(b.firstDate);
          break;
        case 'lastDate':
          cmp = a.lastDate.localeCompare(b.lastDate);
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [displayRows, sortCol, sortDir]);

  if (displayRows.length === 0) {
    return (
      <Box p={8} textAlign="center" color="gray.500">
        <Text>No transactions to group for the selected period.</Text>
      </Box>
    );
  }

  const SortIcon: React.FC<{ col: SortColumn }> = ({ col }) => {
    if (sortCol !== col) return null;
    return sortDir === 'asc' ? (
      <TriangleUpIcon ml={1} boxSize={3} />
    ) : (
      <TriangleDownIcon ml={1} boxSize={3} />
    );
  };

  const thProps = (col: SortColumn) => ({
    cursor: 'pointer',
    userSelect: 'none' as const,
    onClick: () => handleSort(col),
    _hover: { color: 'blue.500' },
    whiteSpace: 'nowrap' as const,
  });

  return (
    <TableContainer>
      <Table size="sm" variant="simple">
        <Thead position="sticky" top={0} zIndex={1} bg={headerBg}>
          <Tr>
            {isEditable && <Th w="40px" />}
            {mode === 'monthly' && (
              <Th color={headerColor} {...thProps('month')}>
                Month <SortIcon col="month" />
              </Th>
            )}
            <Th color={headerColor} {...thProps('normalizedMerchant')}>
              Merchant <SortIcon col="normalizedMerchant" />
            </Th>
            <Th color={headerColor} isNumeric {...thProps('count')}>
              # Txns <SortIcon col="count" />
            </Th>
            <Th color={headerColor} isNumeric {...thProps('totalAmount')}>
              Total <SortIcon col="totalAmount" />
            </Th>
            <Th color={headerColor} {...thProps('firstDate')}>
              First <SortIcon col="firstDate" />
            </Th>
            <Th color={headerColor} {...thProps('lastDate')}>
              Last <SortIcon col="lastDate" />
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {sorted.map((row) => {
            // A group row is "selected" if ALL its source transactions are in selectedIds
            const isSelected =
              isEditable &&
              row.sourceTransactions.length > 0 &&
              row.sourceTransactions.every((t) => selectedIds.has(t.id));

            const amountColor =
              row.totalAmount > 0
                ? 'green.600'
                : row.totalAmount < 0
                ? 'red.500'
                : 'gray.400';

            const title =
              mode === 'monthly'
                ? `${row.normalizedMerchant} — ${row.month}`
                : `${row.normalizedMerchant} — Consolidated`;

            return (
              <Tr
                key={row.rowKey}
                _hover={{ bg: rowHoverBg, cursor: 'pointer' }}
                borderBottomColor={borderColor}
                onClick={() => onRowClick(row.sourceTransactions, title)}
                bg={isSelected ? 'orange.50' : undefined}
              >
                {isEditable && (
                  <Td onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      isChecked={isSelected}
                      onChange={() => {
                        if (onGroupSelect) {
                          onGroupSelect(row.sourceTransactions.map((t) => t.id));
                        }
                      }}
                      colorScheme="orange"
                    />
                  </Td>
                )}

                {mode === 'monthly' && (
                  <Td fontSize="xs" color="gray.500" fontFamily="mono">
                    {row.month}
                  </Td>
                )}

                <Td>
                  <Text fontWeight="medium" fontSize="sm">
                    {row.normalizedMerchant}
                  </Text>
                </Td>

                <Td isNumeric>
                  <Text fontSize="sm" color="gray.600">
                    {row.count}
                  </Text>
                </Td>

                <Td isNumeric>
                  <Text fontWeight="semibold" fontSize="sm" color={amountColor}>
                    {formatSigned(row.totalAmount)}
                  </Text>
                </Td>

                <Td fontSize="xs" color="gray.500" fontFamily="mono">
                  {formatDateUs(row.firstDate)}
                </Td>

                <Td fontSize="xs" color="gray.500" fontFamily="mono">
                  {formatDateUs(row.lastDate)}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
};
